/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { isPlatformServer } from "@angular/common";
import { Injectable, PLATFORM_ID, inject } from "@angular/core";
import {
	type ActivatedRouteSnapshot,
	type CanActivate,
	Router,
	type RouterStateSnapshot,
	type UrlTree,
} from "@angular/router";
import type { Session } from "@supabase/supabase-js";
import { type Observable, catchError, map, throwError } from "rxjs";
import { CbRoutesUtils } from "../@classes/routes/cb-routes-utils";
import { AuthService } from "../@services/shared/auth.service";
import { ErrorHandlerService } from "../@services/shared/error-handler.service";
import { LoggerService } from "../@services/shared/logger.service";
import { AuthStateService } from "../@services/shared/auth-state.service";

@Injectable({
	providedIn: "root",
})
export class SuperadminAuthGuard implements CanActivate {
	private readonly loginUrl = CbRoutesUtils.paths.auth.login.path;

	private readonly router: Router = inject(Router);
	public errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
	public loggerService: LoggerService = inject(LoggerService);

	private readonly platformId = inject(PLATFORM_ID);
	private readonly authService: AuthService = inject(AuthService);
	private readonly authStateService: AuthStateService =
		inject(AuthStateService);

	canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot,
	):
		| Observable<boolean | UrlTree>
		| Promise<boolean | UrlTree>
		| boolean
		| UrlTree {
		this.loggerService.logInfo(
			"[AdminAuthGuard] Checking if user is authenticated",
		);

		if (isPlatformServer(this.platformId)) {
			return true;
		}

		const checkSession = (session: Session | null) => {
			if (!session) {
				throw new Error("No session");
			}

			if (session.user.role !== "superadmin") {
				this.loggerService.logWarning(
					"User not a superadmin, redirecting to tenants page.",
				);
				this.router.navigate([CbRoutesUtils.paths.tenant.path]);
				return false;
			}

			return true;
		};

		if (this.authStateService.currentAuthSession()) {
			return checkSession(this.authStateService.currentAuthSession());
		}

		return this.authService.updateSession().pipe(
			map((session) => {
				return checkSession(session);
			}),
			catchError((error) =>
				throwError(() => this.errorHandlerService.handleError(error, false)),
			),
			catchError((error) => {
				this.loggerService.logError("No session found");
				return throwError(() => this.router.navigate([this.loginUrl]));
			}),
		);
	}
}
